import { useEffect, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  RefreshCw,
  X,
} from "lucide-react";
import { cn } from "@base/ui";
import {
  useSincronizar,
  useStatusSincronizacao,
} from "../application/use-referencias";

const TEMPO_SUCESSO_MS = 4_000;
const TEMPO_ERRO_MS = 6_000;

/**
 * Toast fixo com progresso da sincronização. Não compete com o cabeçalho.
 */
export function ToastDeSincronizacao() {
  const sincronizacao = useStatusSincronizacao();
  const sincronizar = useSincronizar();
  const [visivel, definirVisivel] = useState(false);
  const [ocultado, definirOcultado] = useState(false);

  const status = sincronizacao.data?.status ?? "idle";
  const rodando =
    sincronizacao.data?.running === true || sincronizar.isPending;
  const percentual = sincronizacao.data?.progressPercent ?? 0;
  const mensagem =
    sincronizacao.data?.message?.trim() ||
    (rodando ? "Preparando sincronização…" : null);

  useEffect(() => {
    if (rodando) {
      definirOcultado(false);
      definirVisivel(true);
      return undefined;
    }

    if (status === "success" || status === "error") {
      definirVisivel(true);
      const tempo = status === "success" ? TEMPO_SUCESSO_MS : TEMPO_ERRO_MS;
      const timer = globalThis.setTimeout(() => {
        definirVisivel(false);
      }, tempo);
      return () => globalThis.clearTimeout(timer);
    }

    definirVisivel(false);
    return undefined;
  }, [rodando, status]);

  if (!visivel || ocultado) {
    return null;
  }

  const titulo =
    status === "success"
      ? "Biblioteca atualizada"
      : status === "error"
        ? "Falha na sincronização"
        : "Sincronizando Instagram";

  const Icone =
    status === "success"
      ? CheckCircle2
      : status === "error"
        ? AlertCircle
        : RefreshCw;

  const tomIcone =
    status === "success"
      ? "bg-success/12 text-success"
      : status === "error"
        ? "bg-destructive/12 text-destructive"
        : "bg-primary/10 text-primary";

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center p-4 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:justify-end">
      <div
        role="status"
        aria-live="polite"
        aria-label={titulo}
        className="pointer-events-auto w-full max-w-sm animate-subir rounded-xl border border-border/80 bg-card p-4 shadow-soft"
      >
        <div className="flex gap-3">
          <div
            className={cn(
              "flex size-9 shrink-0 items-center justify-center rounded-lg",
              tomIcone,
            )}
          >
            <Icone
              className={cn("size-4", rodando && "motion-safe:animate-spin")}
              aria-hidden="true"
            />
          </div>

          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-semibold tracking-tight text-foreground">
                {titulo}
              </p>
              {rodando ? (
                <span className="shrink-0 tabular-nums text-sm font-semibold text-foreground">
                  {percentual}%
                </span>
              ) : null}
            </div>

            {mensagem ? (
              <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                {mensagem}
              </p>
            ) : null}

            {rodando ? (
              <div
                role="progressbar"
                aria-valuenow={percentual}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Progresso da sincronização"
                className="h-1.5 overflow-hidden rounded-full bg-muted"
              >
                <div
                  className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out motion-reduce:transition-none"
                  style={{ width: `${percentual}%` }}
                />
              </div>
            ) : null}
          </div>

          {!rodando ? (
            <button
              type="button"
              onClick={() => definirOcultado(true)}
              className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Fechar aviso"
            >
              <X className="size-4" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
